import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { submitAvailability } from '../utils/leadConnector';
type FormState = {
  market: string;
  email: string;
  name: string;
  mlsSoftware: string;
};
type Errors = Partial<Record<keyof FormState, string>>;
type Step = 'area' | 'details' | 'success';
const mlsOptions = ['FlexMLS', 'Matrix', 'Paragon', 'Rapattoni', 'Other / not sure'];
const ease = [0.23, 1, 0.32, 1] as const;
export function AvailabilityForm() {
  const [form, setForm] = useState<FormState>({
    market: '',
    email: '',
    name: '',
    mlsSoftware: ''
  });
  const [errors, setErrors] = useState<Errors>({});
  const [step, setStep] = useState<Step>('area');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const marketRef = useRef<HTMLInputElement>(null);
  const mlsRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (step === 'area') marketRef.current?.focus();
    if (step === 'details') mlsRef.current?.focus();
  }, [step]);
  const update = (key: keyof FormState, value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const handleAreaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = validateArea(form);
    setErrors(next);
    if (Object.keys(next).length === 0) setStep('details');
  };
  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = validateDetails(form);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      await submitAvailability(form);
      setStep('success');
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };
  return <AnimatePresence mode="wait" initial={false} data-id="element-1" data-mp="2bkyaz97ru1r6">
      {step === 'area' && <motion.form key="area" noValidate onSubmit={handleAreaSubmit} exit={{
      opacity: 0,
      x: -8
    }} transition={{
      duration: 0.15,
      ease
    }} data-id="element-2" data-mp="21vrmr4f0efvy">
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-3" data-mp="1sw4mwn1xhi50">
            <TextField id="market" label="Your market" placeholder="Your city or MLS" value={form.market} error={errors.market} autoComplete="address-level2" inputRef={marketRef} onChange={v => update('market', v)} data-id="element-4" data-mp="1sxpga9ciav1a" />
            <TextField id="email" label="Email" type="email" placeholder="Your email" value={form.email} error={errors.email} autoComplete="email" onChange={v => update('email', v)} data-id="element-5" data-mp="2bv0d7nao1cdk" />
          </div>
          <SubmitButton loading={false} loadingLabel="" label="Check my area" data-id="element-6" data-mp="38do8gb5qagfw" />
        </motion.form>}

      {step === 'details' && <motion.form key="details" noValidate onSubmit={handleDetailsSubmit} initial={{
      opacity: 0,
      x: 8
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-7" data-mp="ry1bg06r8mi8">
          <div className="mb-4 flex items-baseline justify-between gap-4" data-id="element-8" data-mp="11pxmscxuq94e">
            <p className="text-sm font-medium text-ink" data-id="element-9" data-mp="dys8w3bh1qwq">Almost there.</p>
            <button type="button" onClick={() => setStep('area')} className="shrink-0 text-sm font-medium text-ink-subtle transition-colors duration-150 hover:text-ink" data-id="element-10" data-mp="8zyveaw6pzwk">
              Change
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-11" data-mp="1zbcyijry0hu6">
            <div data-id="element-12" data-mp="1z9s20om2ioto">
              <label htmlFor="mlsSoftware" className="sr-only" data-id="element-13" data-mp="1zdfx3s75vjai">
                MLS software
              </label>
              <div className="relative" data-id="element-14" data-mp="3vo92b591vjt">
                <select ref={mlsRef} id="mlsSoftware" value={form.mlsSoftware} onChange={e => update('mlsSoftware', e.target.value)} aria-invalid={!!errors.mlsSoftware} aria-describedby={errors.mlsSoftware ? 'mlsSoftware-error' : undefined} className={`${fieldClass(!!errors.mlsSoftware)} appearance-none pr-11 ${form.mlsSoftware ? 'text-ink' : 'text-ink-subtle'}`} data-id="element-15" data-mp="3wpw7dzji8hw">
                  <option value="" disabled data-id="element-16" data-mp="wk26aooiknub">
                    Your MLS software
                  </option>
                  {mlsOptions.map(o => <option key={o} value={o} className="text-ink" data-id="element-17" data-mp="10l5o3gq4gty6">
                      {o}
                    </option>)}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" data-id="element-18" data-mp="1aj1xa2zi1etn" />
              </div>
              {errors.mlsSoftware && <p id="mlsSoftware-error" className="mt-1.5 pl-5 text-xs font-medium text-brand" data-id="element-19" data-mp="1fr1nfxq4s9i9">
                  {errors.mlsSoftware}
                </p>}
            </div>
            <TextField id="name" label="Your name" placeholder="Your name" value={form.name} error={errors.name} autoComplete="name" onChange={v => update('name', v)} data-id="element-20" data-mp="16q3k7hnio60q" />
          </div>
          <SubmitButton loading={submitting} loadingLabel="Submitting…" label="Submit" data-id="element-21" data-mp="1tmdi654adzz6" />
          {submitError && <p role="alert" className="mt-3 text-center text-sm font-medium text-brand" data-id="element-22" data-mp="2m4r5harzy3k0">
              Something went wrong sending your details. Please try again.
            </p>}
        </motion.form>}

      {step === 'success' && <motion.div key="success" initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.22,
      ease
    }} role="status" className="flex items-start gap-3" data-id="element-23" data-mp="fhtj5sgr1jgm">
          <CheckCircle2Icon className="mt-0.5 h-6 w-6 shrink-0 text-brand" aria-hidden="true" data-id="element-24" data-mp="apa5ywlz4qcc" />
          <div data-id="element-25" data-mp="3suvlchvfth3o">
            <p className="text-lg font-semibold text-ink" data-id="element-26" data-mp="3sv59s10u3g79">Thanks, {form.name.split(' ')[0]}.</p>
            <p className="text-ink-muted" data-id="element-27" data-mp="1pdnxdu3apq9">{form.market} is on our map. We’ll be in touch.</p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
function SubmitButton({
  loading,
  label,
  loadingLabel
}: {
  loading: boolean;
  label: string;
  loadingLabel: string;
}) {
  return <button type="submit" disabled={loading} className="mt-3 flex h-[52px] w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand px-7 text-base font-semibold text-white transition-colors duration-150 hover:bg-brand-dark disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-28" data-mp="1h5cjzqqfmva5">
      {loading ? <>
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-29" data-mp="3gduhpnytdk79" />
          {loadingLabel}
        </> : label}
    </button>;
}
type TextFieldProps = {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  error?: string;
  type?: string;
  autoComplete?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onChange: (value: string) => void;
};
function TextField({
  id,
  label,
  value,
  placeholder,
  error,
  type = 'text',
  autoComplete,
  inputRef,
  onChange
}: TextFieldProps) {
  return <div data-id="element-30" data-mp="3uahhrzm2cqhe">
      <label htmlFor={id} className="sr-only" data-id="element-31" data-mp="3uara3tukz9q9">
        {label}
      </label>
      <input ref={inputRef} id={id} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete} onChange={e => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={`${fieldClass(!!error)} text-ink`} data-id="element-32" data-mp="24xw5xcc94k2c" />
      {error && <p id={`${id}-error`} className="mt-1.5 pl-5 text-xs font-medium text-brand" data-id="element-33" data-mp="1nvp0ny2yfk4k">
          {error}
        </p>}
    </div>;
}
function fieldClass(hasError: boolean) {
  return `h-[52px] w-full rounded-full border bg-white px-5 text-base outline-none transition-colors duration-150 placeholder:text-ink-subtle focus:border-ink focus:ring-1 focus:ring-ink ${hasError ? 'border-brand' : 'border-line'}`;
}
function validateArea(form: FormState): Errors {
  const errors: Errors = {};
  if (!form.market.trim()) errors.market = 'Enter your market.';
  if (!form.email.trim()) errors.email = 'Enter your email.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email.';
  return errors;
}
function validateDetails(form: FormState): Errors {
  const errors: Errors = {};
  if (!form.mlsSoftware) errors.mlsSoftware = 'Choose your MLS software.';
  if (!form.name.trim()) errors.name = 'Enter your name.';
  return errors;
}